import { useContext, useEffect, useRef, useState } from "react";
import DynamicForm from "../../../components/DynamicForm/DynamicForm";
import { UserContext } from "../../../context/UserContext";

import {
  text_validation,
  email_validation,
  UseValidation,
  phone_validation,
  username_validation,
} from "../../../components/DynamicForm/Helpers/validationMethods";
import { format_phone } from "../../../components/DynamicForm/Helpers/formattingMethods";
import { Alert, formatPhoneNumber, GetHash, TruncateText } from "../../../utils/genericUtils";
import { LoaderContext } from "../../../context/LoaderContext";
import { PATCH, POST } from "../../../utils/httpUtils";
import { AlertContext } from "../../../context/AlertContext";
import { useNavigate } from "react-router-dom";

const defaultProps = {
  onChanged: () => {},
};

export default function AccountSettings(props = defaultProps) {
  const { user, loginUser } = useContext(UserContext);
  const { show, showloader, hideloader } = useContext(LoaderContext);
  const { globalAlert } = useContext(AlertContext);

  const router = useNavigate();

  const valid = useRef(new Map());

  UseValidation(valid);

  const isValid = () => ![...valid.current.values()].some((v) => v === false);
  const [enableSubmission, setEnableSubmission] = useState(isValid());
  const [edited, setEdited] = useState(false);

  const no_phone_text = "No phone number on record.";

  const [formData, setFormData] = useState({
    firstName: user.firstName,
    lastName: user.lastName,
    username: user.username,
    email: user.email,
    phone: user.phone,
  });

  const getAccountSettingsFields = () => [
    {
      id: "id-input",
      name: "Account Id",
      type: "text",
      disabled: true,
      hoverText: "non-editable field",
      value: user.id,
    },
    {
      id: "first-name-input",
      name: "First Name",
      type: "text",
      placeholder: "First Name",
      value: formData.firstName,
      defaultValue: user.firstName,
      validationFunc: text_validation,
      onChange: (e) => onChange("firstName", e.target.value),
    },
    {
      id: "last-name-input",
      name: "Last Name",
      type: "text",
      placeholder: "Last Name",
      value: formData.lastName,
      defaultValue: user.lastName,
      validationFunc: text_validation,
      onChange: (e) => onChange("lastName", e.target.value),
    },
    {
      id: "username-input",
      name: "Username",
      type: "text",
      placeholder: "Username",
      value: formData.username,
      defaultValue: user.username,
      validationFunc: username_validation,
      onChange: (e) => onChange("username", e.target.value),
    },
    {
      id: "email-input",
      name: "Email",
      type: "email",
      placeholder: "email@example.com",
      disabled: user.isOauth ?? false,
      value: formData.email,
      defaultValue: user.email,
      validationFunc: email_validation,
      onChange: (e) => onChange("email", e.target.value),
    },
    {
      id: "phone-input",
      name: "Phone Number",
      type: "tel",
      placeholder: "555-555-5555",
      value: formData.phone ? formatPhoneNumber(formData.phone) : no_phone_text,
      defaultValue: formData.phone ? formatPhoneNumber(formData.phone) : no_phone_text,
      maxLen: 12,
      disabled: true,
      hoverText: "Update account phone number through multi-factor auth settings",
      validationFunc: phone_validation,
      onChange: (e) => {
        format_phone(e);
        onChange("phone", e.target.value);
      },
    },
  ];

  const onChange = (field, value) => {
    setEdited(true);
    setEnableSubmission(isValid());
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      email: user.email,
      phone: user.phone,
    });
    setEdited(false);
    setEnableSubmission(isValid());
    //clear all validation messages
    let form = document.querySelector("#account-settings-form");
    let validationMessages = form.querySelectorAll(".validation-message");
    validationMessages.forEach((msg) => (msg.textContent = ""));
  };

  const submit = async () => {
    let update_email = formData.email !== user.email;

    showloader();
    let response = await PATCH("/edit-account", { ...formData }, user);
    hideloader();

    if (response.success) {
      setEdited(false);

      if (response.user) {
        loginUser({ ...response.user, isOauth: user.isOauth, settings: user.settings });
      }

      if (!update_email) {
        Alert(globalAlert, "success", "Account Information Updated");
      } else {
        Alert(globalAlert, "success", "Account Information Updated", "you will need to verify the new email address before we update it.", () =>
          router(`/verify-email?id=${user.id}`)
        );
      }
    } else {
      if (response.error) {
        Alert(globalAlert, "error", "Failed to Update Account Info", response.error.message);
      } else {
        Alert(globalAlert, "error", "Failed to Update Account Info", "Something Went Wrong");
      }
    }
  };

  if (!user) return;

  return (
    <>
      <DynamicForm id={"account-settings-form"} layout={[2, 3]} fields={getAccountSettingsFields()} />
      {/* submit/cancel */}
      <div className="settings-list-controls">
        <button className="standard-button btn-clr" onClick={resetForm} disabled={!edited}>
          Cancel
        </button>
        <button className="standard-button btn-clr" disabled={!enableSubmission || !edited} onClick={submit}>
          Save
        </button>
      </div>
    </>
  );
}
